import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseQCOperator, Dependencies, FieldDesc } from '90.quickConnect.Models/models';
import { useStore } from '30.quickConnect.Stores';
import evaluateVisibility from '20.formLib/helpers/evals/evaluateVisibility';
import evalDependencies from '20.formLib/helpers/evalDependencies/evalDependencies';

const useData = (
  id: string,
  fullPathId: string,
  visible: BaseQCOperator,
  desactivatedAutomation: boolean,
  updateFieldVisibility: (hidableFieldFullPathId: string, newVisibility: boolean, forcedByScript?: boolean) => void,
  clearFieldVisibilityCache: (hidableFieldFullPathId: string) => void,
  flattenFields: FieldDesc[],
): void => {
  const {
    LoginStore: {
      signInInfos: { userParameterValue, userUPN, email },
    },
  } = useStore();

  const previousEvalResult = useRef<boolean | undefined>();
  const hasEvaluatedOnce = useRef<boolean>(false);
  const hasMounted = useRef<boolean>(false);

  const { t } = useTranslation('declaration');

  const visibilityDependencies = useMemo(() => {
    const dependencies = evalDependencies(visible, [], flattenFields, fullPathId);
    const newVisibilityDependency: Dependencies = {
      id: id,
      fullPathId: fullPathId,
      rule: visible,
      dependencies: dependencies,
    };
    return newVisibilityDependency;
  }, [visible, flattenFields, id, fullPathId]);

  // Quand le champ disparait (ex: suppression d'une ligne de groupe repetable), on purge le cache de visibilite
  // pour que le champ recree au meme fullPathId soit reevalue a partir de son etat reel.
  useLayoutEffect(() => () => clearFieldVisibilityCache(fullPathId), [fullPathId, clearFieldVisibilityCache]);

  const evaluate = useCallback(() => {
    if (visibilityDependencies) {
      // Si desactivatedAutomation=true (mode consultation ou étape workflow inactive),
      // évaluer une seule fois au montage initial, puis arrêter
      if (desactivatedAutomation && hasEvaluatedOnce.current) {
        return;
      }

      const { rule, dependencies } = visibilityDependencies;
      const evalResult = evaluateVisibility(t, rule, dependencies, userParameterValue, fullPathId, userUPN, email);
      if (previousEvalResult.current !== evalResult) {
        updateFieldVisibility(fullPathId, evalResult);
        previousEvalResult.current = evalResult;
      }

      if (desactivatedAutomation) {
        hasEvaluatedOnce.current = true;
      }
    }
  }, [
    visibilityDependencies,
    updateFieldVisibility,
    userParameterValue,
    t,
    fullPathId,
    userUPN,
    email,
    desactivatedAutomation,
  ]);

  // 1ere evaluation avant que le navigateur ne peigne: un champ cree visible par defaut (ex: nouvelle ligne de groupe
  // repetable) est masque tout de suite, sinon il apparait une fraction de seconde puis disparait.
  useLayoutEffect(() => {
    if (hasMounted.current) return;
    evaluate();
  }, [evaluate]);

  // Evaluations suivantes apres le rendu, pour ne pas retarder l'affichage pendant la saisie.
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    evaluate();
  }, [evaluate]);
};

export default useData;
